import { Component } from '@theme/component';

/** @param {HTMLTableElement} table */
function guideRows(table) {
  return Array.from(table.rows, (row) => Array.from(row.cells, (cell) => cell.textContent.trim()));
}

/** Read the supplier's paired inch/cm columns without converting or rounding values.
 * @param {HTMLTableElement} table
 */
function readHorizontalGuide(table) {
  const rows = guideRows(table);
  if (rows.length < 3 || rows[0][0] !== '' || rows[1][0] !== '') return null;
  const sizes = rows[0].slice(1);
  if (!sizes.length || sizes.some((size) => !size)) return null;
  const width = sizes.length * 2 + 1;
  if (rows.slice(1).some((row) => row.length !== width)) return null;
  if (Array.from(table.rows[0].cells).slice(1).some((cell) => cell.colSpan !== 2)) return null;
  if (Array.from(table.rows).slice(1).some((row) => Array.from(row.cells).some((cell) => cell.colSpan !== 1 || cell.rowSpan !== 1))) return null;
  if (sizes.some((_, i) => !/^inch(?:es)?$/i.test(rows[1][i * 2 + 1]) || !/^cm$/i.test(rows[1][i * 2 + 2]))) return null;
  const measures = rows.slice(2);
  if (measures.some((row) => row.some((value) => !value))) return null;
  return {
    headers: measures.map((row) => row[0]),
    rows: sizes.map((size, i) => ({
      size,
      values: measures.map((row) => ({ inch: row[i * 2 + 1], cm: row[i * 2 + 2] })),
    })),
  };
}

/** Read existing separate unit tables, retaining their measurement order and exact text.
 * @param {HTMLTableElement} cmTable
 * @param {HTMLTableElement} inchTable
 */
function readVerticalGuide(cmTable, inchTable) {
  const cm = guideRows(cmTable);
  const inch = guideRows(inchTable);
  if (cm.length < 2 || cm.length !== inch.length || cm[0].length < 2) return null;
  if (cm[0].some((value, i) => value !== inch[0][i]) || cm[0][0].toLowerCase() !== 'size') return null;
  const width = cm[0].length;
  if ([...cm, ...inch].some((row) => row.length !== width || row.some((value) => !value))) return null;
  if ([cmTable, inchTable].some((table) => Array.from(table.rows).some((row) => Array.from(row.cells).some((cell) => cell.colSpan !== 1 || cell.rowSpan !== 1)))) return null;
  if (cm.some((row, i) => row[0] !== inch[i][0])) return null;
  return {
    headers: cm[0].slice(1),
    rows: cm.slice(1).map((row, i) => ({
      size: row[0],
      values: row.slice(1).map((value, j) => ({ cm: value, inch: inch[i + 1][j + 1] })),
    })),
  };
}

/** Formats rich text tables and progressively enhances AOTD product size guides. */
class RTEFormatter extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#formatTables();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#formatTables();
  }

  #formatTables() {
    if (this.dataset.sizeGuideLabel) this.#formatSizeGuides();
    for (const table of this.querySelectorAll('table')) {
      if (table.closest('.aotd-size-guide') || table.parentElement?.classList.contains('rte-table-wrapper')) continue;
      const wrapper = document.createElement('div');
      wrapper.className = 'rte-table-wrapper';
      table.before(wrapper);
      wrapper.append(table);
    }
  }

  #formatSizeGuides() {
    for (const region of this.querySelectorAll('.aotd-product-copy [aria-label="Size guide in cm"]')) {
      const details = region.closest('details');
      const inchRegion = details?.querySelector('[aria-label="Size guide in inches"]');
      const inchDetails = inchRegion?.closest('details');
      const cmTable = region.querySelector('table');
      const inchTable = inchRegion?.querySelector('table');
      if (!cmTable || !inchTable || !inchDetails || inchDetails === details || inchDetails.children.length !== 2) continue;
      const guide = readVerticalGuide(cmTable, inchTable);
      if (!guide) continue;
      const card = this.#createGuide(guide);
      region.replaceWith(card);
      inchDetails.remove();
    }
    for (const table of this.querySelectorAll('table[id="size-guide"]')) {
      if (table.closest('.aotd-size-guide')) continue;
      const guide = readHorizontalGuide(table);
      if (!guide) continue;
      const card = this.#createGuide(guide);
      card.querySelector('table').id = table.id;
      const target = table.parentElement?.classList.contains('rte-table-wrapper') ? table.parentElement : table;
      target.replaceWith(card);
    }
  }

  /** @param {NonNullable<ReturnType<typeof readHorizontalGuide>>} guide */
  #createGuide(guide) {
    const card = document.createElement('div');
    card.className = 'aotd-size-guide';
    const toolbar = document.createElement('div');
    toolbar.className = 'aotd-size-guide__toolbar';
    const label = document.createElement('span');
    label.className = 'aotd-size-guide__label';
    label.textContent = this.dataset.sizeGuideLabel;
    const controls = document.createElement('div');
    controls.className = 'aotd-size-guide__units';
    controls.setAttribute('role', 'group');
    controls.setAttribute('aria-label', this.dataset.sizeGuideUnits);
    for (const [unit, text, name] of [['cm', 'CM', this.dataset.sizeGuideCm], ['inch', 'IN', this.dataset.sizeGuideInch]]) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'aotd-size-guide__unit';
      button.dataset.unit = unit;
      button.textContent = text;
      button.setAttribute('aria-label', name);
      button.setAttribute('aria-pressed', String(unit === 'cm'));
      button.setAttribute('on:click', '/changeSizeGuideUnit');
      controls.append(button);
    }
    toolbar.append(label, controls);
    const table = document.createElement('table');
    const caption = table.createCaption();
    caption.className = 'visually-hidden';
    caption.textContent = this.dataset.sizeGuideCm;
    const head = table.createTHead().insertRow();
    for (const text of [this.dataset.sizeGuideSize, ...guide.headers]) {
      const cell = document.createElement('th');
      cell.scope = 'col';
      cell.textContent = text;
      head.append(cell);
    }
    const body = table.createTBody();
    for (const row of guide.rows) {
      const tr = body.insertRow();
      const size = document.createElement('th');
      size.scope = 'row';
      size.textContent = row.size;
      tr.append(size);
      for (const value of row.values) {
        const cell = tr.insertCell();
        cell.dataset.cm = value.cm;
        cell.dataset.inch = value.inch;
        cell.textContent = value.cm;
      }
    }
    const status = document.createElement('span');
    status.className = 'visually-hidden';
    status.setAttribute('role', 'status');
    card.append(toolbar, table, status);
    return card;
  }

  /** Native buttons keep focus and keyboard operation when the unit changes.
   * @param {MouseEvent} event
   */
  changeSizeGuideUnit(event) {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement)) return;
    const unit = button.dataset.unit;
    const guide = button.closest('.aotd-size-guide');
    if (!guide || !this.contains(guide) || !['cm', 'inch'].includes(unit) || button.getAttribute('aria-pressed') === 'true') return;
    for (const cell of guide.querySelectorAll('td[data-cm][data-inch]')) cell.textContent = cell.dataset[unit];
    for (const control of guide.querySelectorAll('button[data-unit]')) control.setAttribute('aria-pressed', String(control.dataset.unit === unit));
    const label = unit === 'cm' ? this.dataset.sizeGuideCm : this.dataset.sizeGuideInch;
    guide.querySelector('caption').textContent = label;
    guide.querySelector('[role="status"]').textContent = label;
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}
